import { useProducts } from '@common/billing/pricing-table/use-products';
import { Trans } from '@ui/i18n/trans';
import { Navbar } from '@common/ui/navigation/navbar/navbar';
import { Footer } from '@common/ui/footer/footer';
import { Fragment, useState } from 'react';
import { UpsellBillingCycle } from '@common/billing/pricing-table/find-best-price';
import { StaticPageTitle } from '@common/seo/static-page-title';
import { PricingTable } from '@common/billing/pricing-table/pricing-table';
import { useSettings } from '@ui/settings/use-settings';
import { PricingPageContent } from './pricing-page-content';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import Slider from 'react-slick';

import React, { useEffect } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';

interface ContentProps {
  content: PricingPageContent;
}

// pricingPage is a new settings key that may not exist yet on sites that
// haven't saved any pricing-page settings, so it can be missing entirely
const emptyPricingPageContent = {} as PricingPageContent;

export function PricingPage() {
  const settings = useSettings();
  const pricingPage = settings.pricingPage as
    | { appearance: PricingPageContent }
    | undefined;
  const appearance = pricingPage?.appearance ?? emptyPricingPageContent;
  const query = useProducts('pricingPage');
  const [selectedCycle, setSelectedCycle] = useState<UpsellBillingCycle>('yearly');

  useEffect(() => {
    AOS.init({
      duration: 1500,
    });
  }, []);

  return (
    <Fragment>
      <StaticPageTitle>
        <Trans message="Pricing" />
      </StaticPageTitle>
      <Navbar
        color="bg"
        darkModeColor="transparent"
        border="border-b"
        menuPosition="pricing-table-page"
      />
      <div className="landing-container">
        <h1 className="Plans_heads" data-aos="fade-left">
          <Trans message="Individual plans" />
        </h1>
      </div>

      <div className="price_bg">
        <div className="landing-container">
          <PricingTable
            selectedCycle={selectedCycle}
            productLoader="pricingPage"
          />
        </div>
      </div>

      <LifeTime content={appearance} />

      <TimePlan content={appearance} />

      <Accounts content={appearance} />

      <Footer className="container mx-auto flex-shrink-0 px-24" />
    </Fragment>
  );
}

const defaultLifetimePlans = [
  {
    image: 'images/homepage/fam_1.png',
    title: 'pCloud for Family',
    description: 'Share your space with up to four family members.',
  },
  {
    image: 'images/homepage/fam_2.png',
    title: 'pCloud Business',
    description: 'Organize your team and collaborate on all business projects.',
  },
  {
    image: 'images/homepage/fam_3.png',
    title: 'Link traffic',
    description: 'Get additional traffic for your shared links.',
  },
];

function LifeTime({ content }: ContentProps) {
  const plans = content.lifetimePlans?.length
    ? content.lifetimePlans
    : defaultLifetimePlans;

  return (
    <section className="life_time">
      <div className="landing-container">
        <div className="time_head">
          <h3 data-aos="fade-right" data-testid="lifetimeHeading">
            {content.lifetimeHeading ||
              'Can I, later on upgrade to large Lifetime Plan?'}
          </h3>
          <p data-aos="fade-left" data-testid="lifetimeSubheading">
            {content.lifetimeSubheading ||
              'Prices are final. No setup fees or other hidden charges!'}
          </p>
        </div>
        <div className="row pt-sm-5 pt-4">
          {plans.map((plan, index) => (
            <div className="col-lg-4 col-md-4 mb-4" key={index}>
              <div className="family_contents" data-aos="zoom-in">
                <div className="family_img">
                  <img
                    src={plan.image}
                    alt={plan.title}
                    data-testid={`lifetime-image-${index}`}
                  />
                </div>
                <h3 data-testid={`lifetime-title-${index}`}>{plan.title}</h3>
                <p data-testid={`lifetime-description-${index}`}>
                  {plan.description}
                </p>
              </div>
            </div>
          ))}
        </div>
        <div className="wants_text">
          <p data-testid="lifetimeFooterText">
            {content.lifetimeFooterText ||
              'Want to explore pCloud? Go for our monthly plans or  Try for Free'}
          </p>
        </div>
      </div>
    </section>
  );
}

const timePlanSliderSettings = {
  dots: false,
  arrows: true,
  infinite: true,
  speed: 500,
  loop: true,
  autoplay: false,
  slidesToShow: 3,
  slidesToScroll: 1,
  responsive: [
    {
      breakpoint: 1200,
      settings: {
        slidesToShow: 3,
        slidesToScroll: 1,
      },
    },
    {
      breakpoint: 991,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 1,
      },
    },
    {
      breakpoint: 600,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
      },
    },
  ],
};

const defaultFeatureCard = {
  image: 'images/homepage/collaps_1.png',
  title: 'Collaboration',
  bullets:
    'Share links and File requests\nInvite users to shared folders\nGet detailed stats for your links\nShare links and File requests\nShare links and File requests',
};
// matches the original template's 5 duplicate cards, and keeps the slide
// count >= slidesToShow so react-slick's infinite-loop cloning doesn't
// miscalculate slide widths and collapse the row into a vertical stack
const defaultFeatures: PricingPageContent['features'] = Array(5).fill(
  defaultFeatureCard,
);

const maxFeatureSlides = 3;

function TimePlan({ content }: ContentProps) {
  const features = content.features?.length ? content.features : defaultFeatures;
  // a carousel only makes sense when there are more cards than fit on
  // screen at once - with fewer, just lay them out in a plain row, which
  // also sidesteps react-slick's infinite-loop cloning quirks at low
  // slide counts (the cause of the cards stacking vertically)
  const needsSlider = features.length > maxFeatureSlides;

  return (
    <section className="one_time_plan">
      <div className="landing-container">
        <div className="privileges_head">
          <h1 data-aos="fade-left" data-testid="featuresHeading">
            {content.featuresHeading || 'Onetime Features included with all plans'}
          </h1>
        </div>

        {needsSlider ? (
          <Slider {...timePlanSliderSettings}>
            {features.map((feature, index) => (
              <FeatureCard key={index} feature={feature} index={index} />
            ))}
          </Slider>
        ) : (
          <div className="row justify-content-center">
            {features.map((feature, index) => (
              <div className="col-lg-4 col-md-4 mb-4" key={index}>
                <FeatureCard feature={feature} index={index} />
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

interface FeatureCardProps {
  feature: PricingPageContent['features'][number];
  index: number;
}
function FeatureCard({ feature, index }: FeatureCardProps) {
  return (
    <div className="collapse_contents">
      <div className="collapse_img">
        <img
          src={feature.image}
          alt={feature.title}
          data-testid={`feature-image-${index}`}
        />
      </div>
      <h2 data-testid={`feature-title-${index}`}>{feature.title}</h2>
      <div className="dtt_width">
        {(feature.bullets || '')
          .split('\n')
          .filter(bullet => bullet.trim())
          .map((bullet, bulletIndex) => (
            <div className="dot_img" key={bulletIndex}>
              <img src="images/homepage/dots_img.png" alt="" />
              <p data-testid={`feature-bullet-${index}-${bulletIndex}`}>
                {bullet}
              </p>
            </div>
          ))}
      </div>
    </div>
  );
}

const defaultPaymentMethods: PricingPageContent['paymentMethods'] = [
  {image: 'images/homepage/bank_img_1.png'},
  {image: 'images/homepage/bank_img_2.png'},
  {image: 'images/homepage/bank_img_3.png'},
  {image: 'images/homepage/bank_img_4.png'},
];

function Accounts({ content }: ContentProps) {
  // older saves could persist this as an object keyed by index instead
  // of an array (see getPricingPageAppearanceDefaults), so normalize
  // before checking length/mapping over it.
  const savedPaymentMethods: PricingPageContent['paymentMethods'] =
    Array.isArray(content.paymentMethods)
      ? content.paymentMethods
      : Object.values(
          content.paymentMethods ??
            ({} as Record<string, PricingPageContent['paymentMethods'][number]>),
        );
  const paymentMethods = savedPaymentMethods.length
    ? savedPaymentMethods
    : defaultPaymentMethods;

  return (
    <section className="account_section">
      <div className="landing-container">
        <div className="row justify-content-center">
          <div className="col-lg-6">
            <div className="account_contents">
              <div className="confirm_img">
                <img src="images/homepage/confirm_img.png" alt="Brand 1" />
              </div>
              <div className="account_contents">
                <h2 data-aos="fade-right">
                  {content.paymentMethodsHeading || '14 days money back guarantee!'}
                </h2>
              </div>
              <div className="account_img">
                {paymentMethods.map((method, index) => (
                  <img
                    key={index}
                    src={method.image}
                    data-aos="zoom-in"
                    alt="image"
                    data-testid={`payment-method-image-${index}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
